import React, { useState, useMemo, useRef, useEffect } from 'react'
import styled from 'styled-components'
import Input from 'components/atoms/Input'
import Checkbox from 'components/atoms/Checkbox'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Chip from '@mui/material/Chip'
import useDebounce from 'utils/hooks/useDebounce'

const TagsMultiSelect = ({
  selectedFileTags = [],
  setSelectedFileTags,
  placeholder = 'Search and select tags...',
  fileTags = []
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [showSelected, setShowSelected] = useState(false)
  const resultsRef = useRef(null)
  const inputRef = useRef(null)
  const debouncedValue = useDebounce(searchQuery, 300)

  // Convert file tags to options format
  const tagOptions = useMemo(() => {
    if (!fileTags) return []

    return fileTags.map(tag => ({
      id: tag.id,
      name: tag.name
    })).sort((a, b) => a.name.localeCompare(b.name))
  }, [fileTags])

  // Filter tags based on search query
  const filteredTags = useMemo(() => {
    if (!debouncedValue) return tagOptions
    
    const query = debouncedValue.toLowerCase().trim()
    return tagOptions.filter(tag => {
      return tag.name.toLowerCase().includes(query)
    })
  }, [tagOptions, debouncedValue])

  // Get selected tag names for display
  const selectedTagNames = useMemo(() => {
    if (!selectedFileTags.length || !tagOptions.length) return []
    return selectedFileTags
      .map(id => tagOptions.find(tag => tag.id === id)?.name)
      .filter(Boolean)
  }, [selectedFileTags, tagOptions])

  const handleTagToggle = (tagId) => {
    const newSelected = selectedFileTags.includes(tagId)
      ? selectedFileTags.filter(id => id !== tagId)
      : [...selectedFileTags, tagId]
    setSelectedFileTags(newSelected)
  }

  const handleClearAll = () => {
    setSelectedFileTags([])
  }

  const handleRemoveTag = (tagId) => {
    setSelectedFileTags(selectedFileTags.filter(id => id !== tagId))
  }

  const handleSearch = (_, query) => {
    setSearchQuery(query)
  }

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        const input = inputRef.current.querySelector('input')
        if (input) {
          input.focus()
        }
      }, 100)
    }
  }, [open])

  const displayText = selectedFileTags.length > 0 
    ? `${selectedFileTags.length} tag${selectedFileTags.length > 1 ? 's' : ''} selected`
    : placeholder

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Container>
        <InputWrapper onClick={() => setOpen(true)}>
          <DisplayInput hasValue={selectedFileTags.length > 0}>
            <DisplayText hasValue={selectedFileTags.length > 0}>
              {displayText}
            </DisplayText>
            {selectedFileTags.length > 0 && (
              <ActionButtons>
                <ActionButton onClick={(e) => { e.stopPropagation(); setShowSelected(!showSelected); }}>
                  {showSelected ? 'Hide' : 'Show'} Selected
                </ActionButton>
                <ActionButton onClick={(e) => { e.stopPropagation(); handleClearAll(); }}>
                  Clear All
                </ActionButton>
              </ActionButtons>
            )}
          </DisplayInput>
          <DropdownIcon>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z"/>
            </svg>
          </DropdownIcon>
        </InputWrapper>

        {/* Selected tags display */}
        {selectedFileTags.length > 0 && showSelected && (
          <SelectedWrapper>
            {selectedTagNames.map((tagName, index) => (
              <StyledChip
                key={selectedFileTags[index]}
                label={tagName}
                onDelete={() => handleRemoveTag(selectedFileTags[index])}
                size="small"
              />
            ))}
          </SelectedWrapper>
        )}

        {open && (
          <Dropdown ref={resultsRef}>
            <SearchWrapper>
              <Input
                ref={inputRef}
                onChange={handleSearch}
                value={searchQuery}
                name='searchQuery'
                placeholder='Search tags...'
                nomarginbottom
              />
            </SearchWrapper>
            
            <ResultsWrapper>
              {filteredTags.length === 0 ? (
                <NoResults>
                  {tagOptions.length === 0 ? 'No tags available' : 'No tags found'}
                </NoResults>
              ) : (
                filteredTags.map(tag => (
                  <TagItem key={tag.id}>
                    <Checkbox
                      label={tag.name}
                      name={tag.id}
                      value={selectedFileTags.includes(tag.id)}
                      onChange={() => handleTagToggle(tag.id)}
                      noMargin
                    />
                  </TagItem>
                ))
              )}
            </ResultsWrapper>
            
            {selectedFileTags.length > 0 && (
              <DropdownFooter>
                <FooterText>{selectedFileTags.length} selected</FooterText>
                <FooterButton onClick={handleClearAll}>Clear All</FooterButton>
              </DropdownFooter>
            )}
          </Dropdown>
        )}
      </Container>
    </ClickAwayListener>
  )
}

export default TagsMultiSelect

const Container = styled.div`
  position: relative;
  width: 100%;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 10px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`

const InputWrapper = styled.div`
  position: relative;
  cursor: pointer;
`

const DisplayInput = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: 6px;
  padding: 10px 35px 10px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  min-height: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
  }
`

const DisplayText = styled.span`
  color: ${({ theme, hasValue }) => hasValue ? theme.colors.grey[700] : theme.colors.grey[500]};
  font-weight: 400;
`

const DropdownIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.54);
  pointer-events: none;
  display: flex;
  align-items: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`

const SelectedWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 100px;
  overflow-y: auto;
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.primary}05;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.primary}10;
`

const StyledChip = styled(Chip)`
  && {
    height: 24px;
    font-size: 11px;
    text-transform: capitalize;

    .MuiChip-deleteIcon {
      font-size: 14px;
    }
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey[200]};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  z-index: 9999;
  margin-top: 4px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const SearchWrapper = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.grey[50]}50;
`

const ResultsWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grey[100]};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grey[400]};
    border-radius: 3px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.grey[500]};
    }
  }
`

const TagItem = styled.div`
  padding: 10px 16px;
  transition: all 0.2s ease;
  text-transform: capitalize;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[50]};
    padding-left: 20px;
  }
`

const NoResults = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 14px;
`

const DropdownFooter = styled.div`
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.colors.grey[50]}50;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FooterText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const FooterButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`